import { Spinner } from "@chakra-ui/react";
import { TextInput } from "components/FormInputs/TextInput";
import type { FormikHelpers } from "formik";
import { Field, Form, Formik } from "formik";
import { cn, isValidAbnOrAcn } from "utils/helpers";
import * as Yup from "yup";

import { CompanyContactInfo } from "newui/features/Company";
import {
	COMPANY_TYPE_OPTIONS,
	CompanyTypeEnum,
} from "newui/features/Company/types/CompanyType.type";

import type { GuestCompanyData } from "../../types/guest-company-data.type";

interface CompanyInfoProps {
	onSubmit: (data: GuestCompanyData) => void;
	onCancel: () => void;
	isLoading?: boolean;
}

const CompanyInfoSchema = Yup.object().shape({
	companyName: Yup.string()
		.trim()
		.required("Company name is required")
		.min(1, "Business name should be at least 1 character")
		.max(80, "Business name should not exceed 80 characters"),
	abn: Yup.string()
		.trim()
		.required("ABN/ACN is required")
		.test(
			"test abn/acn",
			"Your ABN/ACN number is invalid",
			(value) => value && isValidAbnOrAcn(value)
		),
	contactName: Yup.string().trim().required("Primary contact name is required"),
	email: Yup.string()
		.trim()
		.email("Invalid email format")
		.required("Primary email is required"),
	phone: Yup.string()
		.trim()
		.required("Primary phone number is required")
		.matches(
			/^(?=.*)((?:\+?61) ?(?:\((?=.*\)))?([2-47-8])\)?|(?:\((?=.*\)))?([0-1][2-47-8])\)?) ?-?(?=.*)((\d{1} ?-?\d{3}$)|(00 ?-?\d{4} ?-?\d{4}$)|( ?-?\d{4} ?-?\d{4}$)|(\d{2} ?-?\d{3} ?-?\d{3}$))/,
			"Invalid Phone Number entered"
		),
	profileType: Yup.number()
		.oneOf(
			[
				CompanyTypeEnum.HeadContractor,
				CompanyTypeEnum.Subcontractor,
				CompanyTypeEnum.Consultant,
			],
			"Please select the company type"
		)
		.required("Company type is required"),
});

export const CompanyInfo: React.FC<CompanyInfoProps> = ({
	onSubmit,
	onCancel,
	isLoading = false,
}) => {
	const initialValues: GuestCompanyData = {
		companyName: "",
		abn: "",
		contactName: "",
		email: "",
		phone: "",
		profileType: undefined,
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={CompanyInfoSchema}
			onSubmit={(
				values: GuestCompanyData,
				{ setSubmitting }: FormikHelpers<GuestCompanyData>
			) => {
				onSubmit(values);
				setSubmitting(false);
			}}
		>
			{({
				values,
				errors,
				touched,
				handleChange,
				setFieldValue,
				isSubmitting,
			}) => (
				<Form className="space-y-4">
					{/* Row 1 - Company Name, ABN, Company Type */}
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
						{/* @ts-ignore */}
						<TextInput
							label="Company Name"
							testId="company-name-input"
							required
							name="companyName"
							type="text"
							value={values.companyName}
							onChange={handleChange}
							error={touched.companyName ? errors.companyName : undefined}
							maxLength={80}
						/>

						{/* @ts-ignore */}
						<TextInput
							label="ABN/ACN"
							testId="company-abn-acn-input"
							name="abn"
							type="text"
							required
							value={values.abn}
							onChange={handleChange}
							setFieldValue={setFieldValue}
							error={touched.abn ? errors.abn : undefined}
						/>
					</div>

					{/* Row 2 - Company Type */}
					<div className="grid grid-cols-1">
						<div
							className={cn(
								"border-opacity-2 space-y-2} border-spacing-2",
								touched.profileType &&
									errors.profileType &&
									"rounded-md p-3 ring-2 ring-red-500 ring-opacity-30"
							)}
						>
							<label className="block pb-2 text-sm font-medium text-gray-700">
								Company Type <span className="text-red-500">*</span>
								<br />
								<span className="font-normal italic text-gray-500">
									Choose one option that best represents your company
								</span>
							</label>
							<div className="flex flex-col gap-2 sm:flex-row sm:gap-4">
								{COMPANY_TYPE_OPTIONS.map((type) => (
									<label
										key={type.value}
										className="flex cursor-pointer items-center space-x-2"
									>
										<Field
											type="radio"
											name="profileType"
											value={type.value}
											checked={values.profileType === type.value}
											onChange={() => setFieldValue("profileType", type.value)}
											className="h-4 w-4 text-blue-600"
										/>
										<span className="text-sm capitalize text-gray-700">
											{type.label}
										</span>
									</label>
								))}
							</div>
							{touched.profileType && errors.profileType && (
								<p className="text-sm text-red-600">{errors.profileType}</p>
							)}
						</div>
					</div>

					{/* Row 3 - Contact Info */}
					<div className="grid grid-cols-1 gap-4 pt-4 md:grid-cols-2 lg:grid-cols-3">
						<CompanyContactInfo onFieldChanged={handleChange} />
					</div>

					{/* Action Buttons */}
					<div className="flex flex-col gap-3 pt-6 sm:flex-row sm:justify-between">
						<button
							onClick={onCancel}
							className="w-full rounded-md border border-gray-300 px-6 py-3 text-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 sm:w-1/2"
							disabled={isLoading || isSubmitting}
						>
							Cancel
						</button>
						<button
							type="submit"
							className="w-full rounded-md bg-blue-600 px-6 py-3 text-lg font-semibold text-white hover:bg-blue-700 disabled:opacity-50 sm:w-1/2"
							disabled={isLoading || isSubmitting}
						>
							{isLoading || isSubmitting ? <Spinner className="mr-2" /> : null}
							{isLoading || isSubmitting ? "Accepting..." : "Accept"}
						</button>
					</div>
				</Form>
			)}
		</Formik>
	);
};
